import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../redux/store';
import { fetchUserData, logout } from '../redux/authSlice';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/app/logo.png';
import { Modal, Fade, Typography } from '@mui/material';
import { CheckCircle, ContentCopy, Share } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell, Camera, Shield, ShieldCheck, Package, RefreshCw,
  RotateCcw, CreditCard, Headphones, Pencil, Store,
  Home, BarChart2, Settings, LogOut, Plus, Copy, Users,
  ChevronRight, Wallet, LayoutDashboard, ClipboardList,
  UserCheck, MapPin, Star, TrendingUp,
} from 'lucide-react';

// ── Palette ────────────────────────────────────────────────
const RED  = '#B91C1C';
const GOLD = '#D97706';
const WHITE = '#FFFFFF';
const BG   = '#F5F5F5';
const DARK = '#111111';
const MUTED = '#888';
const GREEN = '#16a34a';

const maskName = (name: string) => {
  if (!name) return '';
  if (name.length <= 2) return name;
  return name.slice(0, 2) + '*'.repeat(name.length - 2);
};
const maskEmail = (email: string) => {
  if (!email) return '';
  const [u, d] = email.split('@');
  if (!d) return email;
  return `${u.slice(0, 3)}***@${d}`;
};

const Profile: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useSelector((s: RootState) => s.auth);
  const userId   = user?.userId;
  const [copied, setCopied]                 = useState(false);
  const [instantRefunds, setInstantRefunds] = useState(false);

  useEffect(() => { dispatch(fetchUserData()); }, [dispatch]);

  const profileCompletion = () => {
    const fields = ['firstName','lastName','emailAddress','phoneNumber','username','profilePicture','userAge','userAddress','verifyEmail'];
    const filled = fields.filter(f => {
      const v = f === 'userAddress'
        ? user?.userAddress?.city && user.userAddress.streetAddress
        : user?.[f as keyof typeof user];
      return !!v;
    });
    return Math.round((filled.length / fields.length) * 100);
  };


  const handleLogout = async () => {
  try {
    await dispatch(logout()).unwrap();
    // Optional: Force navigation to home or login after success
    navigate('/'); 
  } catch (error) {
    console.error("Logout failed:", error);
  }
};

  const isVerified = user?.isVerified === true;
  const completion = profileCompletion();
  const firstName  = maskName(user?.firstName || 'First');
  const lastName   = maskName(user?.lastName  || 'Last');
  const email      = maskEmail(user?.emailAddress || user?.email || 'email@example.com');
  const photo      = user?.profilePicture || user?.photoURL || null;
  const initials   = `${user?.firstName?.[0]||''}${user?.lastName?.[0]||''}`.toUpperCase() || 'U';

  // ── Logged-out view ───────────────────────────────────────
  if (!user) {
    return (
      <div style={M.root}>
        <style>{WAVE_CSS + RESP_CSS}</style>
        <div className="mobile-only" style={M.mobileWrapper}>
          <div style={M.header}>
            <div style={M.circle1}/><div style={M.circle2}/><div style={M.circle3}/>
            <div style={M.topBar}><span style={M.pageTitle}>Profile</span></div>
          </div>
          <div style={{ padding:'32px 20px', textAlign:'center' }}>
            <h2 style={{ color:DARK, fontSize:22, fontWeight:800, marginBottom:10 }}>Your Profile</h2>
            <p style={{ color:MUTED, marginBottom:24 }}>Login to start planning your next event</p>
            <button onClick={() => navigate('/AuthSwitch')} style={M.redBtn}>Login</button>
            <p style={{ color:MUTED, marginTop:14 }}>
              Don't have an account?{' '}
              <span style={{ color:RED, cursor:'pointer', fontWeight:700 }} onClick={() => navigate('/AuthSwitch')}>Register</span>
            </p>
            <div style={{ margin:'30px 0 20px', height:1, backgroundColor:'#E0E0E0' }} />
            {PUBLIC_LINKS.map(item => (
              <div key={item.label} onClick={item.fn} style={{ display:'flex', justifyContent:'space-between', borderBottom:'1px solid #F0F0F0', paddingBottom:10, cursor:'pointer', marginBottom:12 }}>
                <span style={{ color:DARK, fontWeight:600 }}>{item.label}</span>
                <ChevronRight size={18} color="#bbb" />
              </div>
            ))}
          </div>
        </div>
        {/* Desktop logged-out */}
        <div className="desktop-only" style={D.root}>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'80vh', gap:20 }}>
            <h2 style={{ color:WHITE, fontSize:32, fontWeight:900 }}>Sign In</h2>
            <p style={{ color:'rgba(255,255,255,0.5)', fontSize:15 }}>Login to access your profile</p>
            <button onClick={() => navigate('/AuthSwitch')} style={{ background:`linear-gradient(to right,${RED},#7f1d1d)`, border:'none', color:WHITE, fontWeight:700, borderRadius:12, padding:'14px 48px', cursor:'pointer', fontSize:15 }}>Login</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Referral section ──────────────────────────────────────
  const ReferralBlock = ({ dark }: { dark?: boolean }) => !user?.username ? null : (
    <div style={dark ? D.referralCard : M.referralCard}>
      <div style={{ fontWeight:800, fontSize:15, color: dark ? WHITE : DARK, marginBottom:6 }}>Refer Friends & Earn €10</div>
      <p style={{ color: dark ? 'rgba(255,255,255,0.5)' : MUTED, fontSize:12, marginBottom:12, lineHeight:'1.5' }}>
        Share your link — you both earn €10 on their first booking.
      </p>
      <div style={{ display:'flex', gap:8, marginBottom:10 }}>
        <input readOnly value={`https://4reva.com/referral/?referral-code=${user.username}`}
          style={{ flex:1, borderRadius:10, padding:'9px 12px', border:`1px solid ${dark?'#333':'#E0E0E0'}`, fontSize:11, color: dark?WHITE:DARK, outline:'none', background: dark?'#111':'#F9F9F9' }}/>
        <button style={{ background:`linear-gradient(to right,${RED},#7f1d1d)`, color:WHITE, fontWeight:700, border:'none', borderRadius:10, padding:'9px 14px', cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontSize:12 }}
          onClick={async () => { await navigator.clipboard.writeText(`https://4reva.com/referral/?referral-code=${user.username}`); setCopied(true); setTimeout(()=>setCopied(false),2000); }}>
          <Copy size={13}/> Copy
        </button>
      </div>
      <button style={{ width:'100%', background:`linear-gradient(to right,${GOLD},#b45309)`, color:WHITE, fontWeight:700, border:'none', borderRadius:10, padding:'11px 0', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, fontSize:13 }}
        onClick={async () => { if(navigator.share) await navigator.share({ title:'Join 4REVA GROUP', text:'Earn rewards!', url:`https://4reva.com/referral/?referral-code=${user.username}` }); else alert('Sharing not supported.'); }}>
        <Users size={15}/> Invite Friends
      </button>
      <Modal open={copied} onClose={()=>setCopied(false)}>
        <Fade in={copied}>
          <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', background:'#111', borderRadius:24, padding:'50px 40px', outline:'none', textAlign:'center' }}>
            <AnimatePresence>
              {copied && (
                <motion.div initial={{scale:0,opacity:0}} animate={{scale:1.2,opacity:1}} exit={{scale:0,opacity:0}} transition={{duration:0.4}}>
                  <CheckCircle style={{fontSize:70,color:'#4caf50'}}/>
                  <Typography variant="h6" sx={{color:'#fff',mt:1}}>Link Copied!</Typography>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Fade>
      </Modal>
    </div>
  );

  return (
    <div style={M.root}>
      <style>{WAVE_CSS + RESP_CSS}</style>

      {/* ══════════ MOBILE ══════════ */}
      <div className="mobile-only" style={M.mobileWrapper}>
        {/* Header */}
        <div style={M.header}>
          <div style={M.circle1}/><div style={M.circle2}/><div style={M.circle3}/>
          <div style={M.topBar}>
            <span style={M.pageTitle}>Profile</span>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              {window.location.hostname==='localhost'
                ? <button onClick={()=>{if(user?.isAdmin||user?.isDirector) window.open(`/OperationsManagement/${userId}`,'_blank');}} style={M.logoBtn}><img src={logo} alt="ops" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}}/></button>
                : <button style={{...M.logoBtn,opacity:0.3,cursor:'not-allowed'}} disabled><img src={logo} alt="ops" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}}/></button>
              }
              <button style={M.bellBtn} onClick={()=>navigate('/Notifications')}><Bell size={18} color={WHITE}/></button>
            </div>
          </div>
          <div style={M.avatarSection}>
            <div style={M.avatarOuter}>
              <div style={M.wave} className="wave1"/>
              <div style={{...M.wave,animationDelay:'1s'}} className="wave2"/>
              <div style={{position:'relative'}}>
                {photo ? <img src={photo} alt="avatar" style={M.avatar}/> : <div style={M.avatarPlaceholder}><span style={M.avatarInitials}>{initials}</span></div>}
                <button style={M.cameraIcon} onClick={()=>navigate(`/EditProfile/${userId}`)}><Camera size={11} color={WHITE}/></button>
                {isVerified && <div style={M.verifiedBadge}><ShieldCheck size={12} color={GREEN}/></div>}
              </div>
            </div>
            <div style={{flex:1}}>
              <div style={M.userName}>{firstName} {lastName}</div>
              <div style={M.userEmail}>{email}</div>
              <div style={isVerified ? M.pillVerified : M.pillPending}>
                {isVerified ? <ShieldCheck size={11} color={GREEN}/> : <Shield size={11} color={GOLD}/>}
                <span style={{fontSize:10,fontWeight:800,color:isVerified?GREEN:GOLD}}>{isVerified?'Verified':'Not Verified'}</span>
              </div>
              <div style={{color:GOLD,fontSize:11,fontWeight:700,marginTop:4}}>{completion}% Complete</div>
            </div>
          </div>
          <div style={M.refundCard}>
         {/*
            <div style={{flex:1,marginRight:12}}>
              <div style={M.refundTitle}>Enable Instant Refunds</div>
              <div style={M.refundSub}>No waiting! Enjoy direct refunds in your wallet!</div>
            </div>
         
         */}
            <label style={{cursor:'pointer'}}>
              <input type="checkbox" checked={instantRefunds} onChange={()=>setInstantRefunds(v=>!v)} style={{display:'none'}}/>
              <div style={{width:46,height:26,borderRadius:13,background:instantRefunds?GOLD:'rgba(255,255,255,0.2)',position:'relative',transition:'background 0.2s',display:'flex',alignItems:'center'}}>
                <div style={{width:20,height:20,borderRadius:'50%',backgroundColor:WHITE,position:'absolute',transition:'transform 0.2s',transform:instantRefunds?'translateX(22px)':'translateX(2px)',boxShadow:'0 1px 4px rgba(0,0,0,0.3)'}}/>
              </div>
            </label>
          </div>
        </div>

            <div style={M.body}>

        {/* Body 
          <div style={M.bannerRow}>
            <button style={M.bannerCard} onClick={()=>navigate(`/CommerceOnboarding/${userId}`)}>
              <div style={M.bannerIcon}><TrendingUp size={18} color={GOLD}/></div>
              <div style={{textAlign:'left'}}><div style={M.bannerSub}>No SHARE points?</div><div style={M.bannerAction}>Earn Today →</div></div>
            </button>
            <button style={M.bannerCard} onClick={()=>navigate('/Reorder')}>
              <div style={M.bannerIcon}><Star size={18} color={GOLD}/></div>
              <div style={{textAlign:'left'}}><div style={M.bannerSub}>No savings yet?</div><div style={M.bannerAction}>Start Shopping!</div></div>
            </button>
          </div>
        
        */}
    

     {/*
     
          <div style={M.actionRow}>
            <MobActionCard icon={<Package size={22} color={RED}/>}     label="My Orders"  highlight onClick={()=>navigate('/MetaClientDashboard')}/>
            <MobActionCard icon={<RefreshCw size={22} color={MUTED}/>} label="Returns"             onClick={()=>navigate('/Returns')}/>
            <MobActionCard icon={<RotateCcw size={22} color={MUTED}/>} label="Buy Again"           onClick={()=>navigate('/Reorder')}/>
          </div>
     */}

          <div style={M.sectionTitle}>My Addresses</div>
          <div style={M.addressCard}>
            <p style={{color:'#555',fontSize:14,marginBottom:12}}>Haven't added a delivery address yet?</p>
            <button style={M.addAddressBtn} onClick={()=>navigate(`/EditProfile/${userId}`)}>
              <Plus size={14} color={RED} style={{marginRight:6}}/> Add a New Address
            </button>
          </div>

          <MobListItem icon={<Headphones size={20} color={DARK}/>}  label="Customer Service"      onClick={()=>navigate('/CustomerSupport')} right={<div style={M.whatsappBtn}><Headphones size={16} color={WHITE}/></div>}/>
          <MobListItem icon={<Pencil size={20} color={DARK}/>}      label="Edit Profile"          onClick={()=>navigate(`/EditProfile/${userId}`)}/>
          <MobListItem icon={<ShieldCheck size={20} color={DARK}/>} label="Verify Profile"        onClick={()=>navigate('/VerifyProfile')}/>
{/*   
<MobListItem icon={<CreditCard size={20} color={DARK}/>}  label="Wallet & Saved Cards" onClick={()=>navigate('/PaymentMethodDashboard')}/>

 <MobListItem icon={<Store size={20} color={DARK}/>}       label="Sell Home Furniture & Decor"      onClick={()=>navigate(`/CommerceOnboarding/${userId}`)}/>

 <MobListItem icon={<BarChart2 size={20} color={DARK}/>}   label="Host Overview"         onClick={handleDashboardNavigation}/>

*/}
          <MobListItem icon={<Home size={20} color={DARK}/>}        label="Dashboard"      onClick={()=>navigate(`/management/${userId}`)}/>
          <MobListItem icon={<Settings size={20} color={DARK}/>}    label="Settings & Privacy"    onClick={()=>navigate(`/ProfileSettings/${userId}`)}/>
          <MobListItem icon={<Bell size={20} color={DARK}/>}        label="Notifications"         onClick={()=>navigate(`/Notifications/${userId}`)}/>

          <ReferralBlock/>

          <button style={M.logoutBtn} onClick={handleLogout} >
            <LogOut size={18} color={WHITE} style={{marginRight:10}}/> Logout
          </button>
        </div>
      </div>

      {/* ══════════ DESKTOP ══════════ */}
      <div className="desktop-only" style={D.root}>

        {/* Hero banner */}
        <div style={D.heroBanner}>
          <div style={D.heroCircle1}/><div style={D.heroCircle2}/><div style={D.heroCircle3}/>

          <div style={D.heroLeft}>
            <div style={D.heroAvatarOuter}>
              <div style={M.wave} className="wave1"/>
              <div style={{...M.wave,animationDelay:'1s'}} className="wave2"/>
              <div style={{position:'relative'}}>
                {photo ? <img src={photo} alt="avatar" style={D.heroAvatar}/> : <div style={D.heroAvatarPlaceholder}><span style={D.heroInitials}>{initials}</span></div>}
                <button style={D.heroCamBtn} onClick={()=>navigate(`/EditProfile/${userId}`)}><Camera size={14} color={WHITE}/></button>
                {isVerified && <div style={D.heroVerBadge}><ShieldCheck size={14} color={GREEN}/></div>}
              </div>
            </div>
            <div>
              <div style={D.heroName}>{firstName} {lastName}</div>
              <div style={D.heroEmail}>{email}</div>
              <div style={{display:'flex',alignItems:'center',gap:10,marginTop:10}}>
                <div style={isVerified ? M.pillVerified : M.pillPending}>
                  {isVerified ? <ShieldCheck size={11} color={GREEN}/> : <Shield size={11} color={GOLD}/>}
                  <span style={{fontSize:10,fontWeight:800,color:isVerified?GREEN:GOLD}}>{isVerified?'Verified':'Not Verified'}</span>
                </div>
                <span style={{color:GOLD,fontSize:12,fontWeight:700}}>{completion}% Complete</span>
              </div>
            </div>
          </div>

          <div style={D.heroRight}>
            <div style={D.heroToggleCard}>
            {/*
              <div>
                <div style={{color:WHITE,fontWeight:700,fontSize:14,marginBottom:3}}>Instant Refunds</div>
                <div style={{color:'rgba(255,255,255,0.55)',fontSize:12}}>Direct refunds to your wallet</div>
              </div>
            */}
              <label style={{cursor:'pointer'}}>
                <input type="checkbox" checked={instantRefunds} onChange={()=>setInstantRefunds(v=>!v)} style={{display:'none'}}/>
                <div style={{width:52,height:28,borderRadius:14,background:instantRefunds?GOLD:'rgba(255,255,255,0.2)',position:'relative',transition:'background 0.2s',display:'flex',alignItems:'center'}}>
                  <div style={{width:22,height:22,borderRadius:'50%',backgroundColor:WHITE,position:'absolute',transition:'transform 0.2s',transform:instantRefunds?'translateX(26px)':'translateX(3px)',boxShadow:'0 1px 4px rgba(0,0,0,0.3)'}}/>
                </div>
              </label>
            </div>
          {/*
            <div style={D.heroStats}>
              <HeroStat icon={<Package size={18} color={GOLD}/>}    label="Orders"  onClick={()=>navigate('/MetaClientDashboard')}/>
              <HeroStat icon={<Wallet size={18} color={GOLD}/>}     label="Wallet"  onClick={()=>navigate('/PaymentMethodDashboard')}/>
              <HeroStat icon={<Star size={18} color={GOLD}/>}       label="Points"  onClick={()=>navigate(`/CommerceOnboarding/${userId}`)}/>
              <HeroStat icon={<TrendingUp size={18} color={GOLD}/>} label="Savings" onClick={()=>navigate('/Reorder')}/>
            </div>
          
          */}
          </div>
        </div>

        {/* 3-column grid */}
        <div style={D.grid}>

          {/* COL 1 — Account */}
          <div style={D.col}>
            <div style={D.colHeader}><span style={D.colTitle}>Account</span></div>
            <DeskListItem icon={<Pencil size={15}/>}     label="Edit Profile"       sub="Update your info"       onClick={()=>navigate(`/EditProfile/${userId}`)}/>
            <DeskListItem icon={<UserCheck size={15}/>}  label="Verify Profile"     sub="Complete verification"  onClick={()=>navigate('/VerifyProfile')}/>
            <DeskListItem icon={<MapPin size={15}/>}     label="My Addresses"       sub="Manage delivery spots"  onClick={()=>navigate(`/EditProfile/${userId}`)} badge="Add"/>
            <DeskListItem icon={<Settings size={15}/>}   label="Settings & Privacy" sub="Security & preferences" onClick={()=>navigate(`/ProfileSettings/${userId}`)}/>
            <DeskListItem icon={<Bell size={15}/>}       label="Notifications"      sub="Alerts & updates"       onClick={()=>navigate('/Notifications')}/>
    <button style={M.logoutBtn} onClick={handleLogout} >
            <LogOut size={18} color={WHITE} style={{marginRight:10}}/> Logout
          </button>
          
                    </div>

          {/* COL 2 — Commerce 
                      <DeskListItem icon={<Store size={15}/>}      label="Sell Furnitures & Decor" sub="List your products"    onClick={()=>navigate(`/CommerceOnboarding/${userId}`)}/>

                  <DeskListItem icon={<Package size={15}/>}    label="My Orders"        sub="Track & manage orders" onClick={()=>navigate(`/HomeDecorClientDashboard/${userId}`)} badge="New"/>
            <DeskListItem icon={<RefreshCw size={15}/>}  label="Returns"          sub="Start a return"        onClick={()=>navigate(`/Returns/${userId}`)}/>
            <DeskListItem icon={<RotateCcw size={15}/>}  label="Buy Again"        sub="Reorder favourites"    onClick={()=>navigate(`/Reorder/${userId}`)}/>

                        <DeskListItem icon={<CreditCard size={15}/>} label="Wallet & Cards"   sub="Payments & balance"    onClick={()=>navigate(`/PaymentMethodDashboard/${userId}`)}/>

          */}
          <div style={D.col}>
            <div style={D.colHeader}><span style={D.colTitle}>HELP</span></div>
    
            <DeskListItem icon={<Headphones size={15}/>} label="Customer Service" sub="Get help instantly"    onClick={()=>navigate('/CustomerSupport')} badge="Chat"/>
          </div>

          {/* COL 3 — Hosting + Referral

          
          */}
          <div style={D.col}>
            <div style={D.colHeader}><span style={D.colTitle}>Hosting</span></div>
            <DeskListItem icon={<Home size={15}/>}          label="LandLord/Agent Dashbord" sub="Manage your listings" onClick={()=>navigate(`/propertydashboard/${userId}`)}/>

         {/*
                    
                    
          <DeskListItem icon={<BarChart2 size={15}/>}     label="Host Overview"    sub="Stats & performance"  onClick={handleDashboardNavigation}/>

         
            <DeskListItem icon={<ClipboardList size={15}/>} label="Become a Host"    sub="Start earning today"  onClick={()=>navigate(`/CommerceOnboarding/${userId}`)}/>

            <div style={{marginTop:20}}>
              <div style={D.colHeader}><span style={D.colTitle}>Referrals</span></div>
              <ReferralBlock dark/>
            </div>
         */}
          </div>

        </div>
      </div>
    </div>
  );
};

export default Profile;

// ── Desktop Nav ───────────────────────────────────────────
const DeskNav = ({ logo, userId, user, navigate, handleDashboard }: any) => (
  <nav style={D.nav}>
    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
      <img src={logo} alt="4REVA GROUP" style={{ height:32, width:32, borderRadius:8 }}/>
      <span style={D.navBrandText}>4REVA GROUP</span>
    </div>
   {/*
    <div style={{ display:'flex', alignItems:'center', gap:4 }}>
      <NavLink icon={<LayoutDashboard size={15}/>} label="Dashboard"  onClick={handleDashboard}/>
      <NavLink icon={<Package size={15}/>}         label="My Orders"  onClick={()=>navigate('/MetaClientDashboard')}/>
      <NavLink icon={<Store size={15}/>}           label="Sell"       onClick={()=>navigate(`/CommerceOnboarding/${userId}`)}/>
    </div>
   
   */}
    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
      <button style={D.navIconBtn} onClick={()=>navigate('/Notifications')}><Bell size={18} color="rgba(255,255,255,0.75)"/></button>
      <button style={D.navIconBtn} onClick={()=>navigate(`/ProfileSettings/${userId}`)}><Settings size={18} color="rgba(255,255,255,0.75)"/></button>
      {window.location.hostname==='localhost'
        ? <button onClick={()=>{if(user?.isAdmin||user?.isDirector) window.open(`/OperationsManagement/${userId}`,'_blank');}} style={D.logoBtn}><img src={logo} alt="ops" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}}/></button>
        : <button style={{...D.logoBtn,opacity:0.3,cursor:'not-allowed'}} disabled><img src={logo} alt="ops" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}}/></button>
      }
    </div>
  </nav>
);

// ── Sub-components ─────────────────────────────────────────
const NavLink = ({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) => (
  <button onClick={onClick}
    style={{ background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:6, color:'rgba(255,255,255,0.7)', fontSize:13, fontWeight:600, padding:'6px 14px', borderRadius:8 }}
    onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.color='#fff';(e.currentTarget as HTMLButtonElement).style.background='rgba(255,255,255,0.1)';}}
    onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.color='rgba(255,255,255,0.7)';(e.currentTarget as HTMLButtonElement).style.background='none';}}>
    {icon}{label}
  </button>
);

const HeroStat = ({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) => (
  <button onClick={onClick}
    style={{ background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:12, padding:'14px 18px', cursor:'pointer', display:'flex', flexDirection:'column', gap:6, alignItems:'center', flex:1 }}
    onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.background='rgba(255,255,255,0.14)';}}
    onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.background='rgba(255,255,255,0.08)';}}>
    {icon}
    <span style={{ color:'rgba(255,255,255,0.55)', fontSize:11, fontWeight:600 }}>{label}</span>
  </button>
);

const DeskListItem = ({ icon, label, sub, onClick, badge }: { icon: React.ReactNode; label: string; sub: string; onClick: () => void; badge?: string }) => (
  <button onClick={onClick} style={D.listItem}
    onMouseEnter={e=>{const b=e.currentTarget as HTMLButtonElement;b.style.borderColor=RED;b.style.boxShadow=`0 4px 16px rgba(185,28,28,0.15)`;}}
    onMouseLeave={e=>{const b=e.currentTarget as HTMLButtonElement;b.style.borderColor='#2a2a2a';b.style.boxShadow='0 2px 8px rgba(0,0,0,0.3)';}}>
    <div style={D.listIconWrap}>{icon}</div>
    <div style={{ flex:1, textAlign:'left' }}>
      <div style={{ fontSize:13, fontWeight:700, color:'rgba(255,255,255,0.9)' }}>{label}</div>
      <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:2 }}>{sub}</div>
    </div>
    {badge
      ? <span style={{ background:RED, color:WHITE, fontSize:10, fontWeight:800, borderRadius:20, padding:'3px 9px' }}>{badge}</span>
      : <ChevronRight size={15} color="#555"/>
    }
  </button>
);

const MobActionCard = ({ icon, label, onClick, highlight }: { icon: React.ReactNode; label: string; onClick: () => void; highlight?: boolean }) => (
  <button onClick={onClick} style={{ flex:1, background:'#fff', borderRadius:14, paddingTop:20, paddingBottom:20, display:'flex', flexDirection:'column', alignItems:'center', gap:8, border:highlight?`1.5px solid ${RED}`:'1.5px solid #E8EAF0', boxShadow:highlight?`0 4px 12px rgba(185,28,28,0.12)`:'0 2px 6px rgba(0,0,0,0.04)', cursor:'pointer' }}>
    {icon}
    <span style={{ fontSize:11, fontWeight:700, color:highlight?RED:'#444', textAlign:'center' }}>{label}</span>
  </button>
);

const MobListItem = ({ icon, label, onClick, right }: { icon: React.ReactNode; label: string; onClick: () => void; right?: React.ReactNode }) => (
  <button onClick={onClick} style={M.listItem}>
    <div style={M.listIconWrap}>{icon}</div>
    <span style={M.listLabel}>{label}</span>
    {right ?? <ChevronRight size={18} color="#bbb" style={{ marginLeft:'auto' }}/>}
  </button>
);

// ── Public links config ────────────────────────────────────
const PUBLIC_LINKS = [
  { label:'Settings',            fn: () => window.location.href='/GlobalSettings' },
  { label:'Accessibility',       fn: () => window.open('/GlobalSettings','_blank') },
  { label:'Learn About Hosting', fn: () => window.open('/LearnHosting','_blank') },
  { label:'Get help',            fn: () => window.open('/Help','_blank') },
  { label:'Terms of Service',    fn: () => window.open('/terms','_blank') },
  { label:'Privacy Policy',      fn: () => window.open('/Privacy','_blank') },
];

// ── CSS strings ────────────────────────────────────────────
const WAVE_CSS = `
  @keyframes waveAnim{0%{transform:scale(1);opacity:0.6}100%{transform:scale(2.4);opacity:0}}
  .wave1{animation:waveAnim 2s infinite}
  .wave2{animation:waveAnim 2s infinite 1s}
`;
const RESP_CSS = `
  .desktop-only{display:none}
  .mobile-only{display:block}
  @media(min-width:900px){
    .desktop-only{display:block!important}
    .mobile-only{display:none!important}
  }
`;

// ── Mobile styles ──────────────────────────────────────────
const M: { [k: string]: React.CSSProperties } = {
  root:          { minHeight:'100vh', backgroundColor:BG, display:'flex', justifyContent:'center' },
  mobileWrapper: { width:'100%', maxWidth:500 },
  header: { position:'relative', overflow:'hidden', backgroundColor:'#1a0000', padding:'0 20px 26px', borderBottomLeftRadius:28, borderBottomRightRadius:28 },
  circle1: { position:'absolute', top:-50, right:-50, width:200, height:200, borderRadius:'50%', backgroundColor:'rgba(217,119,6,0.22)', pointerEvents:'none' },
  circle2: { position:'absolute', bottom:-40, left:-40, width:160, height:160, borderRadius:'50%', backgroundColor:'rgba(185,28,28,0.30)', pointerEvents:'none' },
  circle3: { position:'absolute', top:30, left:'35%', width:100, height:100, borderRadius:'50%', backgroundColor:'rgba(255,255,255,0.04)', pointerEvents:'none' },
  topBar:  { display:'flex', flexDirection:'row', alignItems:'center', justifyContent:'space-between', paddingTop:54, marginBottom:22 },
  pageTitle: { color:WHITE, fontSize:22, fontWeight:800 },
  logoBtn:   { width:36, height:36, borderRadius:'50%', background:'rgba(255,255,255,0.12)', border:'none', cursor:'pointer', overflow:'hidden', padding:0 },
  bellBtn:   { width:36, height:36, borderRadius:'50%', backgroundColor:'rgba(255,255,255,0.12)', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' },
  avatarSection: { display:'flex', alignItems:'center', gap:16, marginBottom:20 },
  avatarOuter:   { position:'relative', width:84, height:84, display:'flex', alignItems:'center', justifyContent:'center' },
  wave:              { position:'absolute', width:84, height:84, borderRadius:'50%', border:`2.5px solid ${RED}`, top:0, left:0 },
  avatar:            { width:76, height:76, borderRadius:'50%', border:`2.5px solid ${WHITE}`, objectFit:'cover', display:'block' },
  avatarPlaceholder: { width:76, height:76, borderRadius:'50%', backgroundColor:RED, border:`2.5px solid ${WHITE}`, display:'flex', alignItems:'center', justifyContent:'center' },
  avatarInitials:    { color:WHITE, fontSize:24, fontWeight:800 },
  cameraIcon:    { position:'absolute', bottom:2, right:2, backgroundColor:DARK, borderRadius:'50%', width:24, height:24, border:`1.5px solid ${WHITE}`, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' },
  verifiedBadge: { position:'absolute', top:-2, left:-2, width:22, height:22, borderRadius:'50%', backgroundColor:'#f0fdf4', border:`1.5px solid ${WHITE}`, display:'flex', alignItems:'center', justifyContent:'center' },
  userName:    { color:WHITE, fontSize:18, fontWeight:800, marginBottom:2 },
  userEmail:   { color:'rgba(255,255,255,0.65)', fontSize:12, fontWeight:500, marginBottom:6 },
  pillVerified:{ display:'inline-flex', alignItems:'center', gap:5, borderRadius:999, padding:'4px 10px', backgroundColor:'#f0fdf4', marginBottom:4 },
  pillPending: { display:'inline-flex', alignItems:'center', gap:5, borderRadius:999, padding:'4px 10px', backgroundColor:'rgba(217,119,6,0.2)', marginBottom:4 },
  refundCard:  { backgroundColor:'rgba(255,255,255,0.10)', borderRadius:14, padding:14, display:'flex', alignItems:'center', justifyContent:'space-between' },
  refundTitle: { color:WHITE, fontWeight:700, fontSize:13, marginBottom:3 },
  refundSub:   { color:'rgba(255,255,255,0.65)', fontSize:11, lineHeight:'16px' },
  body:        { padding:'20px 16px 40px' },
  bannerRow:   { display:'flex', gap:12, marginBottom:16 },
  bannerCard:  { flex:1, backgroundColor:'#fff', borderRadius:14, padding:12, display:'flex', alignItems:'center', gap:10, boxShadow:'0 2px 6px rgba(0,0,0,0.05)', border:'none', cursor:'pointer', textAlign:'left' },
  bannerIcon:  { width:36, height:36, backgroundColor:'#fef3c7', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center' },
  bannerSub:   { fontSize:10, color:MUTED, marginBottom:2 },
  bannerAction:{ fontSize:12, fontWeight:700, color:RED },
  actionRow:   { display:'flex', gap:10, marginBottom:20 },
  sectionTitle:{ fontSize:16, fontWeight:700, color:DARK, marginBottom:10 },
  addressCard: { backgroundColor:'#fff', borderRadius:14, padding:20, textAlign:'center', marginBottom:12, boxShadow:'0 2px 6px rgba(0,0,0,0.04)' },
  addAddressBtn:{ border:`1.5px solid #CCD6F0`, borderRadius:10, padding:'8px 24px', background:'none', cursor:'pointer', color:RED, fontWeight:600, fontSize:14, display:'flex', alignItems:'center', justifyContent:'center' },
  listItem:    { backgroundColor:'#fff', borderRadius:14, padding:'14px 16px', display:'flex', alignItems:'center', marginBottom:10, width:'100%', boxShadow:'0 2px 6px rgba(0,0,0,0.04)', border:'1px solid #F0F0F0', cursor:'pointer', textAlign:'left' },
  listIconWrap:{ width:38, height:38, borderRadius:10, backgroundColor:'#F5F5F5', display:'flex', alignItems:'center', justifyContent:'center', marginRight:14, flexShrink:0 },
  listLabel:   { fontSize:14, fontWeight:600, color:DARK },
  whatsappBtn: { backgroundColor:'#25D366', borderRadius:'50%', width:36, height:36, display:'flex', alignItems:'center', justifyContent:'center', marginLeft:'auto' },
  logoutBtn:   { width:'100%', borderRadius:14, padding:'15px 16px', background:`linear-gradient(to right,${RED},#7f1d1d)`, border:'none', cursor:'pointer', color:WHITE, fontWeight:800, fontSize:15, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:8 },
  referralCard:{ backgroundColor:'#fff', borderRadius:14, padding:18, border:'1px solid #F0F0F0', boxShadow:'0 2px 6px rgba(0,0,0,0.04)', marginTop:10, marginBottom:16 },
  redBtn:      { background:`linear-gradient(to right,${RED},#7f1d1d)`, border:'none', color:WHITE, fontWeight:700, borderRadius:30, padding:'12px 36px', cursor:'pointer', fontSize:16 },
};

// ── Desktop styles ─────────────────────────────────────────
const D: { [k: string]: React.CSSProperties } = {
  root: { minHeight:'100vh', backgroundColor:'#0d0d0d' },
  nav: { position:'sticky', top:0, zIndex:100, backgroundColor:'#1a0000', borderBottom:'1px solid rgba(185,28,28,0.3)', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 40px', height:64 },
  navBrandText: { color:WHITE, fontSize:18, fontWeight:900, letterSpacing:'-0.5px' },
  navIconBtn:   { width:36, height:36, borderRadius:8, background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.1)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' },
  logoBtn:      { width:34, height:34, borderRadius:'50%', background:'rgba(255,255,255,0.12)', border:'none', cursor:'pointer', overflow:'hidden', padding:0 },

  heroBanner: { position:'relative', overflow:'hidden', background:'linear-gradient(135deg,#1a0000 0%,#2d0a0a 40%,#1a0f00 100%)', padding:'48px 56px 44px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:48, borderBottom:'1px solid rgba(185,28,28,0.2)' },
  heroCircle1: { position:'absolute', top:-80, right:-80, width:320, height:320, borderRadius:'50%', backgroundColor:'rgba(217,119,6,0.15)', pointerEvents:'none' },
  heroCircle2: { position:'absolute', bottom:-60, left:-60, width:260, height:260, borderRadius:'50%', backgroundColor:'rgba(185,28,28,0.20)', pointerEvents:'none' },
  heroCircle3: { position:'absolute', top:'20%', left:'40%', width:180, height:180, borderRadius:'50%', backgroundColor:'rgba(255,255,255,0.02)', pointerEvents:'none' },

  heroLeft:  { display:'flex', alignItems:'center', gap:28 },
  heroAvatarOuter: { position:'relative', width:110, height:110, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 },
  heroAvatar:            { width:96, height:96, borderRadius:'50%', border:`3px solid ${WHITE}`, objectFit:'cover', display:'block' },
  heroAvatarPlaceholder: { width:96, height:96, borderRadius:'50%', backgroundColor:RED, border:`3px solid ${WHITE}`, display:'flex', alignItems:'center', justifyContent:'center' },
  heroInitials:  { color:WHITE, fontSize:32, fontWeight:800 },
  heroCamBtn:    { position:'absolute', bottom:4, right:4, backgroundColor:DARK, borderRadius:'50%', width:30, height:30, border:`2px solid ${WHITE}`, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' },
  heroVerBadge:  { position:'absolute', top:-2, left:-2, width:26, height:26, borderRadius:'50%', backgroundColor:'#f0fdf4', border:`2px solid ${WHITE}`, display:'flex', alignItems:'center', justifyContent:'center' },
  heroName:      { color:WHITE, fontSize:30, fontWeight:900, letterSpacing:'-0.5px' },
  heroEmail:     { color:'rgba(255,255,255,0.55)', fontSize:13, marginTop:4 },
  heroRight:     { display:'flex', flexDirection:'column', gap:16, flex:1, maxWidth:580 },
  heroToggleCard:{ backgroundColor:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:14, padding:'16px 22px', display:'flex', alignItems:'center', justifyContent:'space-between' },
  heroStats:     { display:'flex', gap:12 },

  grid: { display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:32, padding:'40px 56px 64px', maxWidth:1400, margin:'0 auto' },
  col:  { display:'flex', flexDirection:'column' },
  colHeader:{ marginBottom:16, paddingBottom:12, borderBottom:'1px solid rgba(185,28,28,0.35)' },
  colTitle: { color:'rgba(255,255,255,0.45)', fontSize:11, fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase' },

  listItem: { backgroundColor:'#161616', border:'1px solid #2a2a2a', borderRadius:12, padding:'13px 16px', display:'flex', alignItems:'center', marginBottom:8, width:'100%', boxShadow:'0 2px 8px rgba(0,0,0,0.4)', cursor:'pointer', textAlign:'left', transition:'border-color 0.18s,box-shadow 0.18s' },
  listIconWrap: { width:34, height:34, borderRadius:8, backgroundColor:'rgba(185,28,28,0.12)', display:'flex', alignItems:'center', justifyContent:'center', marginRight:12, flexShrink:0, color:RED },
  logoutBtn: { marginTop:14, borderRadius:12, padding:'13px 16px', background:`linear-gradient(to right,${RED},#7f1d1d)`, border:'none', cursor:'pointer', color:WHITE, fontWeight:800, fontSize:14, display:'flex', alignItems:'center', gap:10, justifyContent:'center' },
  referralCard: { backgroundColor:'#161616', borderRadius:12, padding:18, border:'1px solid #2a2a2a', marginTop:4 },
};